import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Icon from 'components/AppIcon';
import useUser from 'hooks/useUser';
import { useGetUserByIdQuery } from 'redux/features/users/userApi';
import { useGetSettingsQuery } from 'redux/features/settings/settingsApi';
import {
  useGetFamilyDetailsByUserIdQuery,
  useSaveBulkFamilyDetailsMutation,
} from 'redux/features/familyDetailsApi';

const JOB_TYPE_OPTIONS = {
  Job: {
    key: 'Job',
    Question: 'Job Title',
    Required: true,
    DisplayNextInput: true,
  },
  Business: {
    key: 'Business',
    Question: 'What type of Business?',
    Required: true,
    DisplayNextInput: true,
  },
  'Self Employed': {
    Question: 'Work specification',
    Required: true,
    DisplayNextInput: true,
  },
  Homemaker: {
    Question: '',
    Required: false,
    DisplayNextInput: false,
  },
  Other: {
    Question: 'Work specification',
    Required: false,
    DisplayNextInput: true,
  },
};

const DISPLAY_MODE_WORK_LABELS = {
  Job: 'Job Title',
  Business: 'Type of Business',
  Other: 'Work Specification',
  Homemaker: 'Homemaker',
  'Self Employed': 'Work Specification',
};

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];
const MARITAL_STATUS_OPTIONS = ['Single', 'Married'];

// ─── InfoField: Display-only field matching profile page style ───
const InfoField = ({ label, value, icon }) => (
  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
    {icon && (
      <div className="flex-shrink-0 mt-1">
        <Icon name={icon} size={20} color="var(--color-primary)" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-xs select-none font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-sm select-none font-semibold text-gray-900 break-words">
        {value || <span className="text-gray-400 italic">Not provided</span>}
      </p>
    </div>
  </div>
);

// ─── FormInput: Reusable input wrapper for edit mode ───
const FormInput = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
      {label} {required && '*'}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const FamilyDetails = ({ userId, isAdminView = false }) => {
  const currentUser = useUser();
  const isAdminOrSuper = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN';

  // Fetch settings to check if update is allowed for employees
  const { data: settingsData } = useGetSettingsQuery();
  const isUpdateAllowedBySettings = settingsData?.data?.is_update_family_detail_allow || false;

  // Employees can edit only if allowed by settings. Admins can always edit.
  const canEdit = isAdminView || isAdminOrSuper || isUpdateAllowedBySettings;

  // Edit mode state — starts in edit mode if admin view, otherwise display mode
  const [isEditMode, setIsEditMode] = useState(isAdminView);

  // Fetch Employee Profile data to check marital_status
  const { data: employeeData, isLoading: isEmployeeLoading } = useGetUserByIdQuery(
    parseInt(userId),
    { skip: !userId },
  );
  const employeeMaritalStatus = employeeData?.data?.marital_status || 'Single';
  const isMarried = employeeMaritalStatus?.toLowerCase() === 'married';

  // Fetch Family Details of the employee
  const {
    data: familyMembers = [],
    isLoading: isFamilyLoading,
    refetch,
  } = useGetFamilyDetailsByUserIdQuery(userId, { skip: !userId });

  const [saveBulkFamilyDetails, { isLoading: isSaving }] = useSaveBulkFamilyDetailsMutation();

  // Unified form states
  const [father, setFather] = useState({
    id: null,
    full_name: '',
    age: '',
    qualification: '',
    job_business: '',
    job_business_type: '',
    job_field_name: '',
    contact_number: '',
  });

  const [mother, setMother] = useState({
    id: null,
    full_name: '',
    age: '',
    qualification: '',
    job_business: '',
    job_business_type: '',
    contact_number: '',
    job_field_name: '',
  });

  const [spouse, setSpouse] = useState({
    id: null,
    full_name: '',
    age: '',
    gender: '',
    qualification: '',
    job_business: '',
    job_business_type: '',
    contact_number: '',
    job_field_name: '',
  });

  const [siblings, setSiblings] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectedWorkType, setSelectedWorkType] = useState({
    father: '',
    mother: '',
    spouse: '',
  });

  // Sync state from API data
  useEffect(() => {
    if (familyMembers && familyMembers.length > 0) {
      const fMember = familyMembers.find((m) => m.relation === 'Father');
      const mMember = familyMembers.find((m) => m.relation === 'Mother');
      const sMember = familyMembers.find((m) => m.relation === 'Spouse');
      setSelectedWorkType({
        father: fMember?.job_business_type,
        mother: mMember?.job_business_type,
        spouse: sMember?.job_business_type,
      });

      const sibMembers = familyMembers.filter(
        (m) => m.relation === 'Sibling' || m.relation === 'siblings',
      );

      if (fMember) {
        setFather({
          id: fMember.id,
          full_name: fMember.full_name || '',
          age: fMember.age || '',
          qualification: fMember.qualification || '',
          job_business: fMember.job_business || '',
          job_field: fMember.job_business || '',
          job_business_type: fMember.job_business_type || '',
          job_field_name: fMember.job_field_name || '',
          contact_number: fMember.contact_number || '',
        });
      } else {
        resetFather();
      }

      if (mMember) {
        setMother({
          id: mMember.id,
          full_name: mMember.full_name || '',
          age: mMember.age || '',
          qualification: mMember.qualification || '',
          job_business: mMember.job_business || '',
          job_business_type: mMember.job_business_type,
          contact_number: mMember.contact_number || '',
          job_field_name: mMember.job_field_name || '',
        });
      } else {
        resetMother();
      }

      if (sMember) {
        setSpouse({
          id: sMember.id,
          full_name: sMember.full_name || '',
          age: sMember.age || '',
          gender: sMember.gender || '',
          qualification: sMember.qualification || '',
          job_business: sMember.job_business || '',
          job_business_type: sMember.job_business_type || '',
          contact_number: sMember.contact_number || '',
          job_field_name: sMember.job_field_name || '',
        });
      } else {
        resetSpouse();
      }

      if (sibMembers.length > 0) {
        setSiblings(
          sibMembers.map((s) => ({
            id: s.id,
            full_name: s.full_name || '',
            age: s.age || '',
            gender: s.gender || '',
            qualification: s.qualification || '',
            marital_status: s.marital_status || 'Single',
          })),
        );
      } else {
        setSiblings([]);
      }
    } else {
      resetFather();
      resetMother();
      resetSpouse();
      setSiblings([]);
    }
  }, [familyMembers]);

  const resetFather = () =>
    setFather({
      id: null,
      full_name: '',
      age: '',
      qualification: '',
      job_business: '',
      job_business_type: '',
      contact_number: '',
      job_field_name: '',
    });
  const resetMother = () =>
    setMother({
      id: null,
      full_name: '',
      age: '',
      qualification: '',
      job_business: '',
      job_business_type: '',
      contact_number: '',
      job_field_name: '',
    });
  const resetSpouse = () =>
    setSpouse({
      id: null,
      full_name: '',
      age: '',
      gender: '',
      qualification: '',
      job_business: '',
      job_business_type: '',
      contact_number: '',
      job_field_name: '',
    });

  // ─── Real-time single-field validator ───
  const validateSingleField = (errorKey, value, rules = {}) => {
    const { required, minLength, isPhone, isAge } = rules;
    let errorMsg = '';

    const trimmed = typeof value === 'string' ? value.trim() : value;

    if (required && !trimmed) {
      errorMsg = rules.label ? `${rules.label} is required` : 'This field is required';
    } else if (minLength && trimmed && trimmed.length < minLength) {
      errorMsg = `Must be at least ${minLength} characters`;
    } else if (isPhone && trimmed && !/^[0-9]{10}$/.test(trimmed)) {
      errorMsg = 'Must be a valid 10-digit mobile number';
    } else if (isAge && trimmed) {
      if (isNaN(trimmed) || parseInt(trimmed) <= 0) {
        errorMsg = 'Enter a valid age';
      }
    }

    setErrors((prev) => {
      const updated = { ...prev };
      if (errorMsg) {
        updated[errorKey] = errorMsg;
      } else {
        delete updated[errorKey];
      }
      return updated;
    });
  };
  const defaultOption = { Question: '', Required: false, DisplayNextInput: false };

  const fatherWork = JOB_TYPE_OPTIONS[selectedWorkType.father] || defaultOption;
  const motherWork = JOB_TYPE_OPTIONS[selectedWorkType.mother] || defaultOption;
  const spouseWork = JOB_TYPE_OPTIONS[selectedWorkType.spouse] || defaultOption;

  // ─── Field rules map for each section ───
  const FATHER_FIELD_RULES = {
    full_name: { required: true, minLength: 3, label: 'Father name' },
    age: { required: true, isAge: true, label: 'Father age', number: true },
    qualification: { required: true, minLength: 2, label: 'Qualification' },
    contact_number: { required: true, isPhone: true, label: 'Contact number' },
    job_business: { required: fatherWork?.Required, minLength: 3 },
    job_field_name: { required: fatherWork?.key === 'Job', minLength: 2, label: 'Job filed' },
    job_business_type: { required: true, label: 'Work Type' },
  };

  const MOTHER_FIELD_RULES = {
    full_name: { required: true, minLength: 3, label: 'Mother name' },
    age: { required: true, isAge: true, label: 'Mother age' },
    qualification: { required: true, minLength: 2, label: 'Qualification' },
    contact_number: { required: true, isPhone: true, label: 'Contact number' },
    job_business: { required: motherWork?.Required, minLength: 3 },
    job_field_name: {
      required: motherWork?.key === 'Job',
      minLength: 2,
      label: 'Job field',
    },
    job_business_type: { required: true, label: 'Work Type is Required' },
  };

  const SPOUSE_FIELD_RULES = {
    full_name: { required: true, minLength: 3, label: 'Spouse name' },
    age: { required: true, isAge: true, label: 'Spouse age' },
    gender: { required: true, label: 'Spouse gender' },
    qualification: { required: true, minLength: 2, label: 'Qualification' },
    contact_number: { required: true, isPhone: true, label: 'Contact number' },
    job_business: { required: spouseWork?.Required, minLength: 3 },
    job_field_name: {
      required: spouseWork?.key === 'Job',
      minLength: 2,
      label: 'Job field',
    },
    job_business_type: { required: true, label: 'Work Type' },
  };

  const SIBLING_FIELD_RULES = {
    full_name: { required: true, minLength: 3, label: 'Sibling name' },
    age: { required: true, isAge: true, label: 'Age' },
    gender: { required: true, label: 'Gender' },
    qualification: { required: true, minLength: 2, label: 'Qualification' },
    marital_status: { required: true, label: 'Marital Status' },
  };

  // ─── onChange handlers with real-time validation ───
  const handleFatherChange = (field, value) => {
    setFather((prev) => ({ ...prev, [field]: value }));
    const rules = FATHER_FIELD_RULES[field];
    if (rules) validateSingleField(`father_${field}`, value, rules);
  };

  const handleMotherChange = (field, value) => {
    setMother((prev) => ({ ...prev, [field]: value }));
    const rules = MOTHER_FIELD_RULES[field];
    if (rules) validateSingleField(`mother_${field}`, value, rules);
  };

  const handleSpouseChange = (field, value) => {
    setSpouse((prev) => ({ ...prev, [field]: value }));
    const rules = SPOUSE_FIELD_RULES[field];
    if (rules) validateSingleField(`spouse_${field}`, value, rules);
  };

  const handleAddSibling = () => {
    setSiblings((prev) => [
      ...prev,
      {
        id: -Date.now(),
        full_name: '',
        age: '',
        gender: '',
        qualification: '',
        marital_status: 'Single',
      },
    ]);
  };

  const handleRemoveSibling = (id) => {
    setSiblings((prev) => prev.filter((sib) => sib.id !== id));
    // Clean up sibling errors
    setErrors((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (key.endsWith(`_${id}`)) delete updated[key];
      });
      return updated;
    });
  };

  const handleSiblingChange = (id, field, value) => {
    setSiblings((prev) => prev.map((sib) => (sib.id === id ? { ...sib, [field]: value } : sib)));
    const rules = SIBLING_FIELD_RULES[field];
    if (rules) validateSingleField(`sib_${field}_${id}`, value, rules);
  };

  // ─── Full-form validation (on submit) — reuses the same rule maps ───
  const runRulesForField = (errorKey, value, rules) => {
    const { required, minLength, isPhone, isAge } = rules;
    const trimmed = typeof value === 'string' ? value.trim() : value;

    if (required && !trimmed)
      return rules.label ? `${rules.label} is required` : 'This field is required';
    if (minLength && trimmed && trimmed.length < minLength)
      return `Must be at least ${minLength} characters`;
    if (isPhone && trimmed && !/^[0-9]{10}$/.test(trimmed))
      return 'Must be a valid 10-digit mobile number';
    if (isAge && trimmed && (isNaN(trimmed) || parseInt(trimmed) <= 0)) return 'Enter a valid age';
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    // Father
    Object.entries(FATHER_FIELD_RULES).forEach(([field, rules]) => {
      const msg = runRulesForField(`father_${field}`, father[field], rules);
      if (msg) newErrors[`father_${field}`] = msg;
    });

    // Mother
    Object.entries(MOTHER_FIELD_RULES).forEach(([field, rules]) => {
      const msg = runRulesForField(`mother_${field}`, mother[field], rules);
      if (msg) newErrors[`mother_${field}`] = msg;
    });

    // Spouse (only if married)
    if (isMarried) {
      Object.entries(SPOUSE_FIELD_RULES).forEach(([field, rules]) => {
        const msg = runRulesForField(`spouse_${field}`, spouse[field], rules);
        if (msg) newErrors[`spouse_${field}`] = msg;
      });
    }

    // Siblings
    siblings.forEach((sib) => {
      Object.entries(SIBLING_FIELD_RULES).forEach(([field, rules]) => {
        const msg = runRulesForField(`sib_${field}_${sib.id}`, sib[field], rules);
        if (msg) newErrors[`sib_${field}_${sib.id}`] = msg;
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit Save action
  const handleSave = async (e) => {
    if (e) e.preventDefault();

    if (!canEdit) {
      toast.error('Changes are locked by the administrator.');
      return;
    }

    if (!validateForm()) {
      toast.error('Please correct the validation errors before saving.');
      return;
    }

    const membersPayload = [];

    // Father
    membersPayload.push({
      id: father.id && father.id > 0 ? father.id : undefined,
      relation: 'Father',
      full_name: father.full_name,
      age: parseInt(father.age),
      gender: 'Male',
      qualification: father.qualification,
      contact_number: father.contact_number,
      job_business: father.job_business,
      job_field_name: father.job_field_name,
      job_business_type: father.job_business_type,
      marital_status: null,
    });

    // Mother
    membersPayload.push({
      id: mother.id && mother.id > 0 ? mother.id : undefined,
      relation: 'Mother',
      full_name: mother.full_name,
      age: parseInt(mother.age),
      gender: 'Female',
      qualification: mother.qualification,
      contact_number: mother.contact_number || null,
      job_business: mother.job_business || null,
      job_business_type: mother.job_business_type || null,
      job_field_name: mother.job_field_name || null,
      marital_status: null,
    });

    // Spouse (only if married)
    if (isMarried) {
      membersPayload.push({
        id: spouse.id && spouse.id > 0 ? spouse.id : undefined,
        relation: 'Spouse',
        full_name: spouse.full_name,
        age: parseInt(spouse.age),
        gender: spouse.gender,
        qualification: spouse.qualification,
        contact_number: spouse.contact_number || null,
        job_business: spouse.job_business || null,
        job_business_type: spouse.job_business_type || null,
        marital_status: 'Married',
        job_field_name: spouse.job_field_name || null,
      });
    }

    // Siblings
    siblings.forEach((sib) => {
      membersPayload.push({
        id: sib.id && sib.id > 0 ? sib.id : undefined,
        relation: 'Sibling',
        full_name: sib.full_name,
        age: parseInt(sib.age),
        gender: sib.gender,
        qualification: sib.qualification,
        marital_status: sib.marital_status,
        contact_number: null,
        job_business: null,
        job_business_type: null,
        job_field_name: null,
      });
    });

    try {
      await saveBulkFamilyDetails({ userId: parseInt(userId), members: membersPayload }).unwrap();
      toast.success('Family details saved successfully!');
      if (!isAdminView) {
        setIsEditMode(false);
      }
      refetch();
    } catch (err) {
      console.error('Failed to save family details:', err);
      toast.error(err?.data?.message || 'Failed to save family details.');
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setErrors({});
    // Re-sync from API data by triggering refetch
    refetch();
  };

  if (isFamilyLoading || isEmployeeLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        <span className="ml-3 text-gray-600">Loading family details...</span>
      </div>
    );
  }

  const inputClasses = (hasError) =>
    `w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${hasError ? 'border-red-500' : 'border-gray-200'}`;

  // ═══════════════════════════════════════════════════
  // ─── DISPLAY MODE (Employee only - Admin skips to edit mode) ─
  // ═══════════════════════════════════════════════════
  if (!isEditMode && !isAdminView) {
    return (
      <div className="space-y-6">
        {/* Edit Button - only for employees when settings allow */}
        {isUpdateAllowedBySettings && (
          <div className="flex justify-end">
            <button
              onClick={() => setIsEditMode(true)}
              className="bg-primary hover:bg-primary-600 text-white rounded-xl px-5 py-2.5 text-sm font-semibold flex items-center space-x-2 shadow-sm transition-all focus:outline-none"
            >
              <Icon name="Pencil" size={16} color="white" />
              <span>Edit Family Details</span>
            </button>
          </div>
        )}

        {/* Father Details - Display */}
        <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-gray-50">
            <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="User" size={24} color="var(--color-primary)" />
              <span>Father Details</span>
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoField label="Name" value={father.full_name} icon="User" />
              <InfoField
                label="Age"
                value={father.age ? `${father.age} years` : ''}
                icon="Calendar"
              />
              <InfoField label="Qualification" value={father.qualification} icon="BookOpen" />
              <InfoField label="Contact No" value={father.contact_number} icon="Phone" />
              <InfoField label="Work Type" value={father.job_business_type} icon="Tag" />
              {father.job_field_name && (
                <InfoField label="Sector" value={father.job_field_name} icon="Briefcase" />
              )}
              {father.job_business && (
                <InfoField
                  label={DISPLAY_MODE_WORK_LABELS[father.job_business_type]}
                  value={father.job_business}
                  icon="Briefcase"
                />
              )}
            </div>
          </div>
        </div>

        {/* Mother Details - Display */}
        <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-gray-50">
            <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="User" size={24} color="#ec4899" />
              <span>Mother Details</span>
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoField label="Name" value={mother.full_name} icon="User" />
              <InfoField
                label="Age"
                value={mother.age ? `${mother.age} years` : ''}
                icon="Calendar"
              />
              <InfoField label="Qualification" value={mother.qualification} icon="BookOpen" />
              <InfoField label="Contact No" value={mother.contact_number} icon="Phone" />
              <InfoField label="Work Type" value={mother.job_business_type} icon="Tag" />
              {mother.job_field_name && (
                <InfoField label="Sector" value={mother.job_field_name} icon="Briefcase" />
              )}
              {mother.job_business && (
                <InfoField
                  label={DISPLAY_MODE_WORK_LABELS[mother.job_business_type]}
                  value={mother.job_business}
                  icon="Briefcase"
                />
              )}
            </div>
          </div>
        </div>

        {/* Spouse Details - Display (only if married) */}
        {isMarried && (
          <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-gray-50">
              <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
                <Icon name="User" size={24} color="#ec4899" />
                <span>Spouse Details</span>
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Name" value={spouse.full_name} icon="User" />
                <InfoField
                  label="Age"
                  value={spouse.age ? `${spouse.age} years` : ''}
                  icon="Calendar"
                />
                <InfoField label="Gender" value={spouse.gender} icon="User" />
                <InfoField label="Qualification" value={spouse.qualification} icon="BookOpen" />
                <InfoField label="Contact No" value={spouse.contact_number} icon="Phone" />
                <InfoField label="Work Type" value={spouse.job_business_type} icon="Tag" />
                {spouse.job_field_name && (
                  <InfoField label="Sector" value={spouse.job_field_name} icon="Briefcase" />
                )}
                {spouse.job_business && (
                  <InfoField
                    label={DISPLAY_MODE_WORK_LABELS[spouse.job_business_type]}
                    value={spouse.job_business}
                    icon="Briefcase"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Siblings - Display */}
        <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-gray-50">
            <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="Users" size={24} color="#6366f1" />
              <span>Siblings Details</span>
            </h2>
          </div>
          <div className="p-6">
            {siblings.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Users" size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">No siblings added yet</p>
              </div>
            ) : (
              <div className="space-y-6">
                {siblings.map((sib, index) => (
                  <div key={sib.id}>
                    {index > 0 && <hr className="border-gray-200 mb-6" />}
                    <p className="text-xs font-bold text-primary bg-primary-50 border border-primary-100 rounded-full px-3 py-1 inline-block mb-4">
                      Sibling #{index + 1}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoField label="Name" value={sib.full_name} icon="User" />
                      <InfoField label="Qualification" value={sib.qualification} icon="BookOpen" />
                      <InfoField
                        label="Age"
                        value={sib.age ? `${sib.age} years` : ''}
                        icon="Calendar"
                      />
                      <InfoField label="Gender" value={sib.gender} icon="User" />
                      <InfoField label="Marital Status" value={sib.marital_status} icon="User" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // ─── EDIT MODE ────────────────────────────────────
  // ═══════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {/* Cancel button - only for employees who can go back to display mode */}
      {!isAdminView && (
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancelEdit}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl px-5 py-2.5 text-sm font-semibold flex items-center space-x-2 transition-all focus:outline-none"
          >
            <Icon name="X" size={16} />
            <span>Cancel</span>
          </button>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Father Details - Edit */}
        <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-gray-50">
            <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="User" size={24} color="var(--color-primary)" />
              <span>Father Details</span>
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormInput label="Name" required error={errors.father_full_name}>
                <input
                  type="text"
                  value={father.full_name}
                  onChange={(e) => handleFatherChange('full_name', e.target.value)}
                  placeholder="Enter full name"
                  className={inputClasses(errors.father_full_name)}
                />
              </FormInput>

              <FormInput label="Age" required error={errors.father_age}>
                <input
                  type="number"
                  value={father.age}
                  onChange={(e) => handleFatherChange('age', e.target.value)}
                  placeholder="Enter age"
                  className={inputClasses(errors.father_age)}
                />
              </FormInput>

              <FormInput label="Qualification" required error={errors.father_qualification}>
                <input
                  type="text"
                  value={father.qualification}
                  onChange={(e) => handleFatherChange('qualification', e.target.value)}
                  placeholder="Enter highest qualification"
                  className={inputClasses(errors.father_qualification)}
                />
              </FormInput>

              <FormInput label="Contact No" required error={errors.father_contact_number}>
                <input
                  type="tel"
                  maxLength="10"
                  value={father.contact_number}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleFatherChange('contact_number', digits);
                  }}
                  placeholder="Enter 10-digit number"
                  className={inputClasses(errors.father_contact_number)}
                />
              </FormInput>

              <FormInput label="Work Type" error={errors.father_job_business_type}>
                <select
                  value={father.job_business_type}
                  onChange={(e) => {
                    handleFatherChange('job_business_type', e.target.value);
                    handleFatherChange('job_business', '');
                    handleFatherChange('job_field_name', '');
                    setSelectedWorkType((prev) => ({ ...prev, father: e.target.value }));
                  }}
                  className={inputClasses(false)}
                >
                  <option value="">Select Option</option>
                  {Object.entries(JOB_TYPE_OPTIONS).map(([key]) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
              </FormInput>
              {fatherWork?.key === 'Job' && (
                <FormInput
                  required={fatherWork?.Required}
                  label={'In Which Sector?'}
                  error={errors.father_job_field_name}
                >
                  <input
                    type="text"
                    value={father.job_field_name}
                    onChange={(e) => {
                      handleFatherChange('job_field_name', e.target.value);
                    }}
                    placeholder="e.g. IT, Construction, Healthcare"
                    className={inputClasses(errors.father_job_field_name)}
                  />
                </FormInput>
              )}
              {fatherWork?.DisplayNextInput && (
                <FormInput
                  required={fatherWork?.Required}
                  label={fatherWork?.Question || 'Work Details'}
                  error={errors.father_job_business}
                >
                  <input
                    type="text"
                    value={father.job_business}
                    onChange={(e) => handleFatherChange('job_business', e.target.value)}
                    className={inputClasses(errors.father_job_business)}
                  />
                </FormInput>
              )}
            </div>
          </div>
        </div>

        {/* Mother Details - Edit */}
        <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-gray-50">
            <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="User" size={24} color="#ec4899" />
              <span>Mother Details</span>
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormInput label="Name" required error={errors.mother_full_name}>
                <input
                  type="text"
                  value={mother.full_name}
                  onChange={(e) => handleMotherChange('full_name', e.target.value)}
                  placeholder="Enter full name"
                  className={inputClasses(errors.mother_full_name)}
                />
              </FormInput>

              <FormInput label="Age" required error={errors.mother_age}>
                <input
                  type="number"
                  value={mother.age}
                  onChange={(e) => handleMotherChange('age', e.target.value)}
                  placeholder="Enter age"
                  className={inputClasses(errors.mother_age)}
                />
              </FormInput>

              <FormInput label="Qualification" required error={errors.mother_qualification}>
                <input
                  type="text"
                  value={mother.qualification}
                  onChange={(e) => handleMotherChange('qualification', e.target.value)}
                  placeholder="Enter highest qualification"
                  className={inputClasses(errors.mother_qualification)}
                />
              </FormInput>
              <FormInput label="Contact No" required error={errors.mother_contact_number}>
                <input
                  type="tel"
                  maxLength="10"
                  value={mother.contact_number}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleMotherChange('contact_number', digits);
                  }}
                  placeholder="Enter 10-digit number"
                  className={inputClasses(errors.mother_contact_number)}
                />
              </FormInput>
              <FormInput label="Work Type" error={errors.mother_job_business_type}>
                <select
                  value={mother.job_business_type}
                  onChange={(e) => {
                    handleMotherChange('job_business_type', e.target.value);
                    setSelectedWorkType((prev) => ({ ...prev, mother: e.target.value }));
                    handleMotherChange('job_business', '');
                    handleMotherChange('job_field_name', '');
                  }}
                  className={inputClasses(errors.mother_job_business_type)}
                >
                  <option value="">Select Option</option>
                  {Object.entries(JOB_TYPE_OPTIONS).map(([key]) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
              </FormInput>
              {motherWork?.key === 'Job' && (
                <FormInput required label={'In Which Sector?'} error={errors.mother_job_field_name}>
                  <input
                    type="text"
                    value={mother.job_field_name}
                    onChange={(e) => {
                      handleMotherChange('job_field_name', e.target.value);
                    }}
                    placeholder="e.g. IT, Construction, Healthcare"
                    className={inputClasses(errors.mother_job_field_name)}
                  />
                </FormInput>
              )}
              {motherWork?.DisplayNextInput && (
                <FormInput
                  required={motherWork?.Required}
                  label={motherWork?.Question || 'Work Details'}
                  error={errors.mother_job_business}
                >
                  <input
                    type="text"
                    value={mother.job_business}
                    onChange={(e) => handleMotherChange('job_business', e.target.value)}
                    className={inputClasses(errors.mother_job_business)}
                  />
                </FormInput>
              )}
            </div>
          </div>
        </div>

        {/* Spouse Details - Edit (only if married) */}
        {isMarried && (
          <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-gray-50">
              <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
                <Icon name="User" size={24} color="#ef4444" />
                <span>Spouse Details</span>
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormInput label="Spouse Name" required error={errors.spouse_full_name}>
                  <input
                    type="text"
                    value={spouse.full_name}
                    onChange={(e) => handleSpouseChange('full_name', e.target.value)}
                    placeholder="Enter full name"
                    className={inputClasses(errors.spouse_full_name)}
                  />
                </FormInput>

                <FormInput label="Age" required error={errors.spouse_age}>
                  <input
                    type="number"
                    value={spouse.age}
                    onChange={(e) => handleSpouseChange('age', e.target.value)}
                    placeholder="Enter age"
                    className={inputClasses(errors.spouse_age)}
                  />
                </FormInput>

                <FormInput label="Gender" required error={errors.spouse_gender}>
                  <select
                    value={spouse.gender}
                    onChange={(e) => handleSpouseChange('gender', e.target.value)}
                    className={inputClasses(errors.spouse_gender)}
                  >
                    <option value="">Select Gender</option>
                    {GENDER_OPTIONS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </FormInput>

                <FormInput label="Qualification" required error={errors.spouse_qualification}>
                  <input
                    type="text"
                    value={spouse.qualification}
                    onChange={(e) => handleSpouseChange('qualification', e.target.value)}
                    placeholder="Enter highest qualification"
                    className={inputClasses(errors.spouse_qualification)}
                  />
                </FormInput>
                <FormInput label="Contact No" required error={errors.spouse_contact_number}>
                  <input
                    type="tel"
                    maxLength="10"
                    value={spouse.contact_number}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleSpouseChange('contact_number', digits);
                    }}
                    placeholder="Enter 10-digit number"
                    className={inputClasses(errors.spouse_contact_number)}
                  />
                </FormInput>
                <FormInput label="Work Type" error={errors.spouse_job_business_type}>
                  <select
                    value={spouse.job_business_type}
                    onChange={(e) => {
                      handleSpouseChange('job_business_type', e.target.value);
                      setSelectedWorkType((prev) => ({ ...prev, spouse: e.target.value }));
                      handleSpouseChange('job_business', '');
                      handleSpouseChange('job_field_name', '');
                    }}
                    className={inputClasses(errors.spouse_job_business_type)}
                  >
                    <option value="">Select Option</option>
                    {Object.entries(JOB_TYPE_OPTIONS).map(([key]) => (
                      <option key={key} value={key}>
                        {key}
                      </option>
                    ))}
                  </select>
                </FormInput>
                {spouseWork?.key === 'Job' && (
                  <FormInput
                    required
                    label={'In Which Sector?'}
                    error={errors.spouse_job_field_name}
                  >
                    <input
                      type="text"
                      value={spouse.job_field_name}
                      onChange={(e) => {
                        handleSpouseChange('job_field_name', e.target.value);
                      }}
                      placeholder="e.g. IT, Construction, Healthcare"
                      className={inputClasses(errors.spouse_job_field_name)}
                    />
                  </FormInput>
                )}
                {spouseWork?.DisplayNextInput && (
                  <FormInput
                    required={spouseWork?.Required}
                    label={spouseWork?.Question || 'Spouse Job/Business'}
                    error={errors.spouse_job_business}
                  >
                    <input
                      type="text"
                      value={spouse.job_business}
                      onChange={(e) => handleSpouseChange('job_business', e.target.value)}
                      className={inputClasses(errors.spouse_job_business)}
                    />
                  </FormInput>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Siblings - Edit */}
        <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-gray-50 flex justify-between items-center">
            <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="Users" size={24} color="#6366f1" />
              <span>Siblings Details</span>
            </h2>
            <button
              type="button"
              onClick={handleAddSibling}
              className="bg-primary hover:bg-primary-600 text-white rounded-xl px-4 py-2 text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-all focus:outline-none"
            >
              <Icon name="Plus" size={14} color="white" />
              <span>Add Sibling</span>
            </button>
          </div>
          <div className="p-6">
            {siblings.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
                <Icon name="Users" size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-xs font-semibold text-gray-400">No siblings added yet</p>
                <p className="text-[10px] text-gray-400 mt-1">Click the button above to add</p>
              </div>
            ) : (
              <div className="space-y-6">
                {siblings.map((sib, index) => (
                  <div key={sib.id}>
                    {index > 0 && <hr className="border-gray-200 mb-6" />}
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs font-bold text-primary bg-primary-50 border border-primary-100 rounded-full px-2.5 py-0.5">
                        Sibling #{index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSibling(sib.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors focus:outline-none"
                        title="Remove sibling"
                      >
                        <Icon name="Trash" size={14} />
                      </button>
                    </div>

                    {/* Row 1: Name + Qualification */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
                      <FormInput label="Name" required error={errors[`sib_full_name_${sib.id}`]}>
                        <input
                          type="text"
                          value={sib.full_name}
                          onChange={(e) => handleSiblingChange(sib.id, 'full_name', e.target.value)}
                          placeholder="Name"
                          className={inputClasses(errors[`sib_full_name_${sib.id}`])}
                        />
                      </FormInput>

                      <FormInput
                        label="Qualification"
                        required
                        error={errors[`sib_qualification_${sib.id}`]}
                      >
                        <input
                          type="text"
                          value={sib.qualification}
                          onChange={(e) =>
                            handleSiblingChange(sib.id, 'qualification', e.target.value)
                          }
                          placeholder="Qualification"
                          className={inputClasses(errors[`sib_qualification_${sib.id}`])}
                        />
                      </FormInput>
                    </div>

                    {/* Row 2: Age + Gender + Marital Status */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <FormInput label="Age" required error={errors[`sib_age_${sib.id}`]}>
                        <input
                          type="number"
                          value={sib.age}
                          onChange={(e) => handleSiblingChange(sib.id, 'age', e.target.value)}
                          placeholder="Age"
                          className={inputClasses(errors[`sib_age_${sib.id}`])}
                        />
                      </FormInput>

                      <FormInput label="Gender" required error={errors[`sib_gender_${sib.id}`]}>
                        <select
                          value={sib.gender}
                          onChange={(e) => handleSiblingChange(sib.id, 'gender', e.target.value)}
                          className={inputClasses(errors[`sib_gender_${sib.id}`])}
                        >
                          <option value="">Select Gender</option>
                          {GENDER_OPTIONS.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </FormInput>

                      <FormInput
                        label="Marital Status"
                        required
                        error={errors[`sib_marital_status_${sib.id}`]}
                      >
                        <select
                          value={sib.marital_status}
                          onChange={(e) =>
                            handleSiblingChange(sib.id, 'marital_status', e.target.value)
                          }
                          className={inputClasses(errors[`sib_marital_status_${sib.id}`])}
                        >
                          {MARITAL_STATUS_OPTIONS.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </FormInput>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={isSaving}
            className="bg-primary hover:bg-primary-600 disabled:opacity-50 text-white rounded-xl px-8 py-3 text-sm font-semibold flex items-center justify-center space-x-2 shadow-md hover:shadow-lg active:scale-98 transition-all w-full sm:w-auto"
          >
            {isSaving ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                <span>Saving details...</span>
              </>
            ) : (
              <>
                <Icon name="Save" size={16} color="white" />
                <span>Save Family Details</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FamilyDetails;
